/**
 * Mock-mode copies of the 16 lesson articles `scripts/seed_ht_course.py`
 * writes (LMS contract section 10). Ids, kinds, minutes, objectives, flow links
 * and knowledge-check questions match `seed/ht_course.json`; bodies are shortened. Anything the
 * document leaves open is marked `PLACEHOLDER:` so a manager can find it.
 */

export const LESSON_FOLDER = { id: 'fld-ht-course', name: 'Hero Trainer Certification', parentId: null };

const PASS = 0.8;

const FRONT_DESK_NOTE = 'PLACEHOLDER: Front Desk is not a role in ACC yet; this module is shown to Hero Trainers and Managers.';

const q = (id, prompt, choices, answer, explanation = '') => ({ id, prompt, choices, answer, explanation });

const check = (questions) => ({ passScore: PASS, questions });

const walkBody = (moduleTitle, sopId, extra = []) => [
    `# ${moduleTitle}: hands-on walk`,
    '',
    'Do this for real at a live booth with a manager or a certified Hero Trainer watching.',
    '',
    ...(sopId
        ? [
            `Run the SOP walkthrough in ACC as you go: [${sopId}](/knowledge-base?article=${sopId}). Finishing it is what reaches Training.`,
            'A manager then confirms it in Training; until then it shows as waiting on a manager.',
        ]
        : ['A manager confirms the walk in Training; until then it shows as waiting on a manager.', '', 'PLACEHOLDER: link the SOP article once written']),
    ...extra,
].join('\n');

const lessonArticle = ({ id, title, body, lesson, updatedDaysAgo = 3 }) => {
    const d = new Date();
    d.setDate(d.getDate() - updatedDaysAgo);
    return {
        id,
        title,
        folderId: LESSON_FOLDER.id,
        status: 'published',
        whenNeeded: 'reference',
        requiredReading: false,
        visibility: [],
        version: 1,
        updatedAt: d.toISOString(),
        updatedBy: 'seed_ht_course',
        createdBy: 'seed_ht_course',
        seededAt: d.toISOString(),
        externalLinks: [],
        steps: [],
        body,
        lesson: {
            kind: 'read',
            objectives: [],
            format: '',
            minutes: 0,
            media: [],
            knowledgeCheck: { passScore: PASS, questions: [] },
            ...lesson,
        },
    };
};

export const seedLessonArticles = () => [
    lessonArticle({
        id: 'lesson-m0-foundations',
        title: 'Company Foundations & Culture',
        body: [
            '# Company Foundations & Culture',
            '',
            '> PLACEHOLDER: welcome video link',
            '',
            'Black Box VR turns high-intensity resistance training and cardio into a competitive video game. In a 30-minute session, members complete 14 to 18 sets of full-body resistance training with HIIT-style cardio and agility. Every rep powers an in-game action.',
            '',
            '## Core beliefs',
            '- **Fitness Improves Lives.** Exercise is the foundation of happiness, energy and self-esteem.',
            '- **Adherence Is the Secret.** Consistency beats any single workout; the whole system makes showing up automatic.',
            '- **Customer Success Is All That Matters.** The company wins only when members get real results.',
            '- **Video Games Are Addictive.** Game design is used deliberately to build a healthy habit.',
            '- **Immersive Tech Is Powerful.** VR affects the brain differently than a mirror or a trainer alone.',
            '- **We Are Stronger Together.** Team-based problem solving is the culture, not a slogan.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'Video or self-paced read',
            minutes: 10,
            objectives: [
                'Explain what Black Box VR is and how the workout model works',
                "Describe the company's core beliefs and how they shape day-to-day behavior on the floor",
            ],
            knowledgeCheck: check([
                q('q1', 'How long is a standard Black Box VR session, and roughly how many resistance sets does it include?',
                    ['45 minutes, 8-10 sets', '30 minutes, 14-18 sets', '60 minutes, 20-25 sets'], 1,
                    'A session is 30 minutes with 14 to 18 sets of full-body resistance training.'),
                q('q2', 'Which core belief emphasizes that consistency matters more than any single workout?',
                    ['Video Games Are Addictive', 'Adherence Is the Secret', 'We Are Stronger Together'], 1,
                    'Adherence Is the Secret: showing up is what the system is built around.'),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m1-booth-hardware',
        title: 'Booth Overview (Hardware)',
        body: [
            '# Booth Overview (Hardware)',
            '',
            '## The machine: Elite 2.2',
            'An automated cable resistance machine with VR tracking. Four stepper motors position the pad and handles for each member; two servo motors provide up to 230 lbs of resistance. ARC (Advance RSS Control) communicates the settings for each exercise.',
            '',
            '**Safety:** the game will not let a member touch the pad or handles while they move. Never override or bypass it.',
            '',
            '## Components',
            '- **Base stations** establish the play space. Solid green means working; red means failed.',
            '- **HMD** is the headset, sanitized in the Clean Box between sessions.',
            '- **Wrist pucks** are color-coded: red on the right wrist, blue on the left.',
            '- **Machine puck** is used for calibration: green OK, blue needs pairing, red replace.',
            '- **Link box**, **handles**, **center pad**, **webcams** and **booth lights** round out the booth.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person, hands-on at a live booth',
            minutes: 20,
            objectives: [
                'Identify every hardware component in a booth and state its function',
                'Explain how the Elite 2.2 machine adjusts to a member automatically',
                'Describe the safety feature that prevents injury during machine movement',
            ],
            knowledgeCheck: check([
                q('q1', 'What color LED on a base station means it needs to be replaced?', ['Green', 'Blue', 'Red'], 2,
                    'Red means the base station failed. Green is working.'),
                q('q2', "What critical safety feature prevents a member from touching the pad/handles while they're moving?",
                    ['An audible alarm', 'Game design locks input during machine motion', 'A physical lock the trainer engages'], 1,
                    'The game locks input while the machine moves. Never bypass it.'),
                q('q3', 'Which wrist puck color goes on the right wrist?', ['Red', 'Blue', "Either, it doesn't matter"], 0,
                    'Red is right, blue is left, matching the arm garments.'),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m1-booth-walk',
        title: 'Booth Overview (Hardware): hands-on walk',
        body: walkBody('Booth Overview (Hardware)', 'sop-booth-prep-for-member-workout', [
            '',
            'Name every component at the booth, show the base station and puck LEDs, and explain the motion lock.',
        ]),
        lesson: {
            kind: 'hands_on',
            format: 'In-person, hands-on at a live booth',
            minutes: 30,
            objectives: ['Name every booth component at a live booth', 'Read base station and puck LEDs correctly'],
            handsOn: { flowKind: 'sop:sop-booth-prep-for-member-workout', cosign: true },
        },
    }),
    lessonArticle({
        id: 'lesson-m2-hardware-repair',
        title: 'Hardware Repair & Replacement',
        body: [
            '# Hardware Repair & Replacement',
            '',
            '## Wear parts',
            '- **Handles and handle cables:** replace the cable when the outer sheath shows wear.',
            '- **Center pad cover:** simple swap when torn or heavily worn.',
            '- **Base stations:** full replacement when the LED is red.',
            '- **Carabiners:** replace when the gate spring is weak or does not close fully.',
            '',
            '## Intervals',
            '- Lightning rope: inspect every 3M lbs until 21M lbs, then every 1M lbs.',
            '- Kevlar handles: inspect every 3M lbs until 9M lbs, then every 1M lbs. Replace once the width has halved.',
            '- Annual: lead screw greasers, PCB fan filter, belts, bearings, fasteners.',
            '- Every 3 years: replace servo and stepper drive belts.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person, hands-on',
            minutes: 15,
            objectives: [
                'Perform routine replacement of wear-and-tear components',
                'Follow correct greasing intervals for machine towers and belts',
                'Know when a component has reached end-of-life and needs escalation vs. simple swap',
            ],
            knowledgeCheck: check([
                q('q1', 'At what cumulative load do Kevlar Handles need replacement, per the wear guideline?',
                    ['When width is reduced by half', 'At exactly 9M lbs regardless of condition', 'Only when they visibly snap'], 0,
                    'Load sets the inspection cadence; the handle is replaced once its width has halved.'),
                q('q2', 'How often should servo and stepper motor drive belts be fully replaced (not just inspected)?',
                    ['Every year', 'Every 3 years', 'Only if they fail'], 1,
                    'Belts are inspected yearly and replaced every 3 years.'),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m2-repair-walk',
        title: 'Hardware Repair & Replacement: hands-on walk',
        body: walkBody('Hardware Repair & Replacement', null, [
            '',
            'Swap a center pad cover and a handle cable, then grease one tower, with a manager watching.',
        ]),
        lesson: {
            kind: 'hands_on',
            format: 'In-person, hands-on',
            minutes: 45,
            objectives: ['Replace a wear component correctly', 'Judge end-of-life against the equipment tracker intervals'],
            handsOn: { flowKind: null, cosign: true },
        },
    }),
    lessonArticle({
        id: 'lesson-m3-software',
        title: 'Software Overview',
        body: [
            '# Software Overview',
            '',
            '> PLACEHOLDER: screen recording links',
            '',
            FRONT_DESK_NOTE,
            '',
            '- **Scheduler:** look up members, move booths, extend or cancel a workout.',
            '- **BCM:** restart booth computers, launch the game, read the Green / Yellow / Red booth status.',
            '- **Splashtop:** view a booth PC remotely. Always reset the video overlay before closing the session.',
            '- **SteamVR:** pair pucks, room setup, quick calibrate, restart the headset.',
            '- **ARC:** Hard-Home calibration, clearing an E-Stop, testing positions.',
            '- **Salesforce:** member lookup, communication history, emergency contacts.',
            '- **GymMaster, Square POS, T-Sheets:** billing, retail, clocking in and out.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'Screen-recorded walkthrough + hands-on practice',
            minutes: 25,
            objectives: [
                'Navigate each core software tool used during a shift',
                'Complete common scheduling, member-lookup, and booth-management tasks unsupervised',
            ],
            knowledgeCheck: check([
                q('q1', 'In BCM, what do the Green / Yellow / Red booth indicators represent?',
                    ['Battery levels', 'Booth operational status', 'Member skill tier'], 1),
                q('q2', "Which tool would you use to look up a member's emergency contact information?",
                    ['Salesforce', 'SteamVR', 'ARC'], 0),
                q('q3', 'What must you do before closing a Splashtop remote session?',
                    ['Nothing, just close it', 'Reset the video overlay', 'Restart the booth PC'], 1,
                    'Leaving the overlay on breaks the booth view for the next person.'),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m4-facility-ops',
        title: 'Facility / Ops Training',
        body: [
            '# Facility / Ops Training',
            '',
            '## Opening',
            'Unlock and disarm (enter the code, wait for confirmation), lights and music at 45-50%, clock in, start headsets, check every booth, inspect the facility.',
            '',
            '## Closing',
            'Reset every booth, power headsets down fully, trash and bathrooms, restock, sweep, wet-mop on Tuesdays and Thursdays, log the daily report, arm the alarm, lock up.',
            '',
            '## Cadence',
            'During shift: wipe walls and sanitize headsets. Weekly: wet-mop Tue/Thu, windows Wed/Fri/Sun, deep clean bathrooms. Monthly: booth wipe-down, wear check, AED and extinguishers. Quarterly: HVAC filters.',
            '',
            '## Standards',
            'FitRadio only at 45-50%, bright clean lighting, HQ-approved scent, no handwritten signs.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person, on shift',
            minutes: 20,
            objectives: [
                'Execute full opening and closing procedures independently',
                'Follow the daily, weekly, monthly, and quarterly cleaning schedule',
                'Maintain the Power-Up Station and facility to brand standard',
            ],
            knowledgeCheck: check([
                q('q1', 'What must you verify before disarming the alarm system in the morning?',
                    ['Nothing, just enter the code', 'Wait for the confirmation after entering the code', 'Call HQ first'], 1),
                q('q2', 'How often is the full facility wet-mopped?', ['Daily', 'Tuesdays and Thursdays', 'Monthly only'], 1),
                q('q3', "What's the approved music volume range during operating hours?", ['20-30%', '45-50%', '70-80%'], 1),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m4-open-walk',
        title: 'Facility / Ops Training: opening walk',
        body: walkBody('Facility / Ops Training', 'sop-gym-opening', [
            '',
            'Finish a real opening walk in ACC. A manager confirms it afterwards.',
        ]),
        lesson: {
            kind: 'hands_on',
            format: 'In-person, on shift',
            minutes: 30,
            objectives: ['Open the gym independently using the guided flow'],
            handsOn: { flowKind: 'open', cosign: true },
        },
    }),
    lessonArticle({
        id: 'lesson-m4-close-walk',
        title: 'Facility / Ops Training: closing walk',
        body: walkBody('Facility / Ops Training', 'sop-gym-closing', [
            '',
            'Finish a real closing walk in ACC. A manager confirms it afterwards.',
        ]),
        lesson: {
            kind: 'hands_on',
            format: 'In-person, on shift',
            minutes: 30,
            objectives: ['Close the gym independently using the guided flow'],
            handsOn: { flowKind: 'close', cosign: true },
        },
    }),
    lessonArticle({
        id: 'lesson-m5-troubleshooting',
        title: 'Troubleshooting Training',
        body: [
            '# Troubleshooting Training',
            '',
            '## Headset',
            '"USB-C charging port disabled": press OK on the white controller. Not connecting: open Virtual Desktop and pick the booth PC, or restart SteamVR and switch to VR.',
            '',
            '## Game',
            'Will not launch: check green LEDs on the machine puck, base stations and wrist trackers. No sound: headset volume, then Voice Meter A1 dropdown. Alignment off: wait 15 seconds, then Space Calibrator continuous calibration.',
            '',
            '## LEDs',
            '- Puck: green OK, blue pair it, red replace, white hold the center button.',
            '- Base station: green OK, red replace.',
            '- E-Stop: blue means engaged. Press it, clear in BoothMinder/ARC, Hard-Home and test 3 positions.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person, hands-on at a live booth',
            minutes: 20,
            objectives: [
                'Diagnose and resolve the most common headset, game, and tracking issues without escalation',
                'Read LED status indicators correctly and take the right action',
                'Know when to escalate a machine E-Stop or hardware failure instead of attempting a fix',
            ],
            knowledgeCheck: check([
                q('q1', 'A tracking puck is showing a blue light. What does that mean and what do you do?',
                    ['It failed, replace it', 'It needs pairing, follow the puck pairing procedure', "It's fully operational, no action needed"], 1),
                q('q2', 'After clearing a machine E-Stop, what must you do before resuming normal use?',
                    ['Nothing further', 'Hard-Home all machines and test 3 positions', "Restart the entire building's power"], 1),
                q('q3', 'If a member reports no sound in their headset, where do you check first?',
                    ['Space Calibrator', "Headset volume, then Voice Meter's A1 dropdown", 'The machine puck LED'], 1),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m5-troubleshooting-walk',
        title: 'Troubleshooting Training: hands-on walk',
        body: walkBody('Troubleshooting Training', 'sop-advanced-alignment-troubleshooting', [
            '',
            'Pair a puck, clear a simulated E-Stop and fix a no-sound headset with a manager watching.',
        ]),
        lesson: {
            kind: 'hands_on',
            format: 'In-person, hands-on at a live booth',
            minutes: 30,
            objectives: ['Resolve the common headset, game and tracking issues at a live booth', 'Recover from an E-Stop safely'],
            handsOn: { flowKind: 'sop:sop-advanced-alignment-troubleshooting', cosign: true },
        },
    }),
    lessonArticle({
        id: 'lesson-m6-customer-service',
        title: 'Customer Service Training',
        body: [
            '# Customer Service Training',
            '',
            'Every member is a hero. Greet immediately, use their name, acknowledge wins. If busy: eye contact, "I\'ll be right with you", then follow up.',
            '',
            '- **Merch:** recommend around goals, never pressure. Square POS or GymMaster only, no cash.',
            '- **Promos:** only HQ-authorized codes, confirmed before checkout.',
            '- **Complaints:** engage immediately, listen fully, resolve or escalate, log in ACC, follow up within 24 hours.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person role-play + shadowing',
            minutes: 15,
            objectives: [
                'Apply approved greeting and service standards consistently',
                'Handle merch sales, demos, scheduling, and promotions correctly',
                "Resolve complaints using the company's complaint-handling process",
            ],
            knowledgeCheck: check([
                q('q1', 'What is the correct approach to selling merchandise to members?',
                    ['Push it proactively on every visit', 'Recommend it naturally around their goals, never pressure', 'Only mention it if they ask'], 1),
                q('q2', 'Within what timeframe must you follow up on a logged complaint?',
                    ['24 hours', '7 days', 'Only if the member follows up first'], 0),
                q('q3', 'What payment methods are accepted at the front desk?',
                    ['Cash and card', 'Credit/debit cards only, through Square POS or GymMaster', 'Checks only'], 1),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m7-safety',
        title: 'Safety & Crisis Management',
        body: [
            '# Safety & Crisis Management',
            '',
            'Stay calm. Act quickly. Communicate clearly. Life safety over equipment. Never hesitate to call 911.',
            '',
            '- **Medical:** assess, call 911, use the AED if needed, keep the area clear, log in ACC under Incident Reports.',
            '- **Fire:** pull the alarm and evacuate. Use an extinguisher only if the fire is very small and you are trained.',
            '- **Equipment:** shut it down, escort the member out, flag the booth out of service.',
            '- **Reporting:** every slip, trip, fall and hardware fault. Stay neutral; never admit fault or assign blame.',
            '',
            'Quarterly refresher required.',
        ].join('\n'),
        lesson: {
            kind: 'read',
            format: 'In-person + required quarterly refresher',
            minutes: 15,
            objectives: [
                'Identify and prevent the most common facility hazards',
                'Respond correctly to a medical emergency, fire, or equipment failure',
                'Complete an incident report accurately in ACC',
            ],
            knowledgeCheck: check([
                q('q1', 'What is the first action in any crisis situation involving potential injury?',
                    ['Document in ACC', 'Assess the situation and call 911 if needed', 'Notify HQ'], 1),
                q('q2', 'Under what condition should you attempt to use a fire extinguisher?',
                    ['Always, immediately', 'Only if the fire is very small and you are trained', 'Never, under any circumstance'], 1),
                q('q3', 'When documenting an incident in ACC, what should you avoid doing?',
                    ['Including the date and time', 'Admitting fault or assigning blame', 'Describing actions taken'], 1),
            ]),
        },
    }),
    lessonArticle({
        id: 'lesson-m8-cpr-aed',
        title: 'CPR / AED Certification',
        body: [
            '# CPR / AED Certification',
            '',
            'Completed through an American Heart Association or American Red Cross affiliated provider. Current at all times; the standard cycle is 2 years.',
            '',
            'Record the provider, completion date, expiration date and a copy of the certificate here. A manager verifies it.',
        ].join('\n'),
        lesson: {
            kind: 'external_cert',
            format: 'External provider',
            minutes: 0,
            objectives: ['Hold a current CPR / AED certification from an AHA or Red Cross affiliated provider'],
            certKind: 'cpr_aed',
        },
    }),
    lessonArticle({
        id: 'lesson-m8-harassment',
        title: 'Sexual Harassment Prevention Training',
        body: [
            '# Sexual Harassment Prevention Training',
            '',
            'Idaho has no state-mandated course, but this is required company-wide for liability protection. Completed through a licensed online provider (Traliant, EVERFI, TrainingToday).',
            '',
            'Record the provider, completion date and certificate of completion here before sign-off.',
        ].join('\n'),
        lesson: {
            kind: 'external_cert',
            format: 'External provider',
            minutes: 0,
            objectives: ['Complete harassment prevention training with a licensed online provider'],
            certKind: 'harassment',
        },
    }),
    lessonArticle({
        id: 'lesson-m9-signoff',
        title: 'Shadowing & Final Competency Check-Off',
        body: [
            '# Shadowing & Final Competency Check-Off',
            '',
            'Complete the shadow shifts the course requires (3 by default; the document recommends 2 to 4, set by the manager) alongside a certified Hero Trainer. A manager observes booth setup and reset, a troubleshooting response and customer service on a live shift, then signs off.',
            '',
            'Once signed off you are cleared to work an independent shift.',
        ].join('\n'),
        lesson: {
            kind: 'signoff',
            format: 'In-person, manager-led',
            minutes: 0,
            objectives: ['Complete the shadow shifts and the live competency check with a manager'],
        },
    }),
];
